import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useMindMapService from '../../hooks/useMindMapService';
import useDocumentCache from '../../hooks/useDocumentCache';
import CustomMindmap, {
    MiniMap,
    Controls,
    Background,
    addEdge,
    ConnectionMode,
} from 'Custom-Mindmap';
import NodeProperties from '../NodeProperties';
import { nodeTypes } from "../Shapes/shapes";
import { DEFAULT_NODE_COLOR, EDGE_OPTIONS, MINIMAP_OPTIONS, minimapColorOf } from "../Shapes/shapeCatalog";
import { SHAPE_COLOR_DRAG_TYPE, SHAPE_DRAG_TYPE } from "../Sidebar/shapeLibrary";
import UndoRedoButtons from "../UndoRedo/UndoRedoButtons";
import MapContextMenu from "../ContextMenu/MapContextMenu";
import MapShortcuts from "../MapActions/MapShortcuts";
import { v4 } from "uuid";
import { MindMapContext } from '../../Context/contex';
import { MindMapAPI, workwiseServices, templateService } from '../../Api/api';
import { useParams } from "react-router-dom";
import { emitEdgeAdd, emitEdgeDelete, emitNodeAdd, emitNodeDelete, emitNodeUpdate, emitRoomID } from '../../Socket/socket';
import { processTemplateData, removeTemplateIdFromURL, updateFileSize } from '../../Utils/helper';
import Sidebar from '../Sidebar';
import WaterMark from '../WaterMark';
import { message } from 'antd';
import Loader from '../SkeletonLoader/Loader';

// Backspace too: Mac keyboards have no Delete key. Keys typed in a field never delete (the engine checks).
const DELETE_KEYS = ['Delete', 'Backspace'];
// The node's top-level name is not shown anywhere (data.label is), so new nodes save it empty.
const NEW_NODE_NAME = '';

const MindMapContainer = ({ contentLoading, setContentLoading }) => {
    const {
        isReadOnly,
        setSelectedNodeId,
        nodes, setNodes,
        edges, setEdges,
        onEdgesChange, onNodesChange,
        selectedNodeId,
        mindmapInstance, setMindmapInstance,
        mindmapWrapper,
        userInfo, userColor, setUserColor, setSavingData,
        isDocumentChanged, setIsDocumentChanged, isExternalAllow, setIsExternalAllow,
        documentInfo, isTemplateDocument, setTemplateData,
        recordChange, clearHistory,
    } = MindMapContext();

    const mindMapService = useMindMapService();

    const isMobile = window.screen.width < 768
    const { documentId } = useParams();

    // Caching applies to normal documents only (never templates / read-only).
    const cacheEnabled = !isTemplateDocument && !isReadOnly;
    const { cached, isUiLocked, lockUi, unlockUi, cacheRenderedRef, commitCache } =
        useDocumentCache(documentId, cacheEnabled);

    // Decided once on mount: is there a renderable cached payload to paint?
    // Used to suppress the loader from the very first render (no flash).
    const renderableCacheRef = useRef(
        cacheEnabled && Array.isArray(cached?.documentData?.nodes)
    );

    // The node open in the properties panel (null when closed).
    const [editingNodeId, setEditingNodeId] = useState(null);
    const [nodePosition, setNodePosition] = useState({});
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);

    const initializeEmptyMap = async () => {
        const defaultNodeID = `default-node-${v4()}`;

        const initialNodeData = {
            id: defaultNodeID,
            label: "",
            nodeBg: DEFAULT_NODE_COLOR,
            textBg: "#000",
            allocatedColor: userColor,
        };

        const initialNode = {
            id: defaultNodeID,
            type: "custom",
            data: initialNodeData,
            position: { x: 650, y: 250 },
        };

        setNodes([initialNode]);

        const nodePayload = {
            creatorId: userInfo.userId,
            id: defaultNodeID,
            documentId,
            name: NEW_NODE_NAME,
            type: "custom",
            position: { x: 650, y: 250 },
            data: initialNodeData,
        };

        await mindMapService.createNode(nodePayload, {
            nodes: [initialNode],
            edges: [],
            isExternal: false,
        });

        emitNodeAdd(nodePayload);
        updateFileSize(nodePayload, setSavingData);
    };

    const hydrateExistingMap = (mindMapData) => {
        const nodesWithDimensions = mindMapData.nodes.map((node) => ({
            ...node,
            data: {
                ...node.data,
                width: node.style?.width,
                height: node.style?.height,
            },
            position: {
                x: node.position?.x || 10,
                y: node.position?.y || 10,
            },
        }));

        setNodes(nodesWithDimensions);
        setEdges(mindMapData.edges);

        const userNode = mindMapData.nodes.find(
            (node) => node.creatorId === userInfo.userId
        );

        if (userNode) {
            setUserColor(userNode.data.allocatedColor);
        }

        updateFileSize(mindMapData, setSavingData);
    };

    // Instant paint: render the cached content immediately on a cache hit, before
    // the background fetch resolves. We deliberately do NOT mark the editor
    // "ready" (contentLoading stays true) so the existing init path still swaps in
    // fresh data when the fetch returns. Edits are locked during the preview.
    useLayoutEffect(() => {
        if (!cacheEnabled || isReadOnly) return;
        if (cacheRenderedRef.current) return;
        const mindMapData = cached?.documentData;
        if (!mindMapData || !Array.isArray(mindMapData.nodes)) return;

        cacheRenderedRef.current = true;
        setIsExternalAllow(mindMapData.isExternal);
        setNodes(mindMapData.nodes);
        setEdges(Array.isArray(mindMapData.edges) ? mindMapData.edges : []);

        const userNode = mindMapData.nodes.find(
            (node) => node.creatorId === userInfo.userId
        );
        if (userNode?.data?.allocatedColor) {
            setUserColor(userNode.data.allocatedColor);
        }

        lockUi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Once the background fetch has replaced the cached preview with fresh data
    // (contentLoading flips false), release the preview lock.
    useEffect(() => {
        if (!contentLoading && isUiLocked) {
            unlockUi();
        }
    }, [contentLoading, isUiLocked, unlockUi]);

    // Write-back: mirror the latest user-visible state into the cache, driven by
    // the in-memory content so unsaved edits win over stale API data. Guarded so
    // the locked cached preview is never written back over fresh data.
    useEffect(() => {
        if (!cacheEnabled || isReadOnly) return;
        if (isUiLocked || contentLoading) return;
        if (!Array.isArray(nodes)) return;

        commitCache(
            { nodes, edges, isExternal: isExternalAllow },
            documentInfo
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes, edges, documentInfo, isUiLocked, contentLoading]);

    useEffect(() => {
        emitRoomID({ documentId });
    }, [documentId]);

    useEffect(() => {
        if (!documentId || isTemplateDocument === null) return;

        const loadMindMapData = async () => {
            setContentLoading(true);

            const searchParams = new URLSearchParams(window.location.search);
            const templateId = searchParams.get("templateId");

            let mindMapData = null;

            // If this is a template flow, fetch template first (prefer query param over path param)
            if (templateId || isTemplateDocument) {
                const templateToFetch = templateId || documentId;
                try {
                    const res = await templateService.fetchTemplateById(templateToFetch);
                    const processedData = processTemplateData(res);
                    mindMapData = processedData?.mindmapData || null;
                    if (isTemplateDocument) {
                        setTemplateData?.(processedData);
                    }
                } catch (err) {
                    console.error("Template fetch failed:", err);
                    message.error("Failed to fetch template data");
                    setContentLoading(false);
                    return;
                }
                // If templateId came from URL → remove it from the url
                if (templateId) {
                    try {
                        removeTemplateIdFromURL();
                        const importPayload = {
                            documentId,
                            nodes: mindMapData?.nodes ?? [],
                            edges: mindMapData?.edges ?? [],
                        };
                        try {
                            await mindMapService.saveImportDataToDb(importPayload);
                        } catch (err) {
                            console.error("Failed to save imported template data to DB:", err);
                        }
                    } catch (err) {
                        console.warn('Failed to remove templateId from URL (non-fatal):', err);
                    }
                }
            } else {
                // Normal document fetch
                try {
                    const res = await MindMapAPI.fetchMindMapById(documentId);
                    mindMapData = res?.data || null;
                } catch (err) {
                    console.error("Mind map fetch failed:", err);
                    message.error("Failed to fetch mind map data");
                    setContentLoading(false);
                    return;
                }
            }
            // Safety Fallback
            if (!mindMapData) {
                console.error("Mind map data not found after fetch");
                message.error("Mind map data not found");
                setContentLoading(false);
                return;
            }
            // Continue normal processing
            try {
                setIsExternalAllow(mindMapData?.isExternal);
                // Empty Map Handling
                if (!mindMapData?.nodes?.length) {
                    await initializeEmptyMap();
                } else {
                    hydrateExistingMap(mindMapData);
                }
            } catch (err) {
                console.error("Error processing mind map data:", err);
                message.error("Failed to process mind map data");
            } finally {
                setContentLoading(false);
            }
        };

        clearHistory();
        loadMindMapData();

    }, [documentId, isTemplateDocument]);

    const onConnect = useCallback((params) => {
        if (isUiLocked) return;
        if (params.source === params.target) {
            return;
        }
        const newEdgeId = "edge-" + v4();
        const newEdge = { id: newEdgeId, documentId: documentId, ...params };
        const updatedEdges = addEdge(newEdge, edges);
        setEdges(updatedEdges);
        recordChange({ nodes, edges }, { nodes, edges: updatedEdges });
        mindMapService.createEdge(newEdge, { nodes, edges: updatedEdges });
        emitEdgeAdd(newEdge);
        setIsDocumentChanged(true);
    }, [setEdges, edges, nodes, mindMapService, isUiLocked, recordChange]);

    const updateNodeDimensions = (nodeId, dimensions) => {
        mindmapWrapper.current[nodeId] = dimensions;
    };
    useEffect(() => {
        const nodeElements = document.querySelectorAll('.cm-node');
        nodeElements.forEach((nodeElement) => {
            const nodeId = nodeElement.getAttribute('data-id');
            const dimensions = nodeElement.getBoundingClientRect();
            updateNodeDimensions(nodeId, dimensions);
        });
    }, [nodes]);

    const handleNodeClick = (event, node) => {
        setSelectedNodeId(node.id);
        setEditingNodeId(node.id);
    };
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback((event) => {
        if (isUiLocked) {
            event.preventDefault();
            return;
        }
        const newNodeId = "node-" + v4();
        const newNodeData = {
            id: newNodeId,
            label: '',
            // The dragged shape's category colour; drags from elsewhere carry none.
            nodeBg: event.dataTransfer.getData(SHAPE_COLOR_DRAG_TYPE) || DEFAULT_NODE_COLOR,
            textBg: '#000',
            allocatedColor: userColor
        }
        event.preventDefault();
        const mindmapBounds = mindmapWrapper.current.getBoundingClientRect();
        const type = event.dataTransfer.getData(SHAPE_DRAG_TYPE);
        const proximityThreshold = 100;
        if (typeof type === 'undefined' || !type) {
            return;
        }
        else {
            const position = mindmapInstance.project({
                x: event.clientX - mindmapBounds.left,
                y: event.clientY - mindmapBounds.top,
            });
            let nearestNode = null;
            let minDistance = Number.MAX_VALUE;
            nodes.forEach((node) => {
                const nodeCenterX = node.position.x + node.width / 2;
                const nodeCenterY = node.position.y + node.height / 2;
                const distance = Math.sqrt(
                    Math.pow(position.x - nodeCenterX, 2) + Math.pow(position.y - nodeCenterY, 2)
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestNode = node;
                }
            });
            if (nearestNode && nearestNode.id && minDistance <= proximityThreshold) {
                const newEdge = {
                    id: `edge-${v4()}`,
                    documentId: documentId,
                    source: nearestNode.id,
                    target: newNodeId,
                    sourceHandle: 'bottom',
                    targetHandle: 'top',
                };

                const newNodePosition = {
                    x: nearestNode.position.x,
                    y: nearestNode.position.y + nearestNode.height + 20,
                };
                const newNode = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    position: newNodePosition,
                    data: newNodeData,
                    type: type,
                    // style: { zIndex: 999 },
                };
                const updatedNodes = [...nodes, newNode];
                const updatedEdges = [...edges, newEdge];
                setEdges(updatedEdges);
                setNodes(updatedNodes);
                recordChange({ nodes, edges }, { nodes: updatedNodes, edges: updatedEdges });
                emitEdgeAdd(newEdge);

                const payload = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    documentId: documentId,
                    name: NEW_NODE_NAME,
                    type: type,
                    position: newNodePosition,
                    data: newNodeData,
                };
                if (isTemplateDocument) {
                    mindMapService.syncTemplate({ nodes: updatedNodes, edges: updatedEdges });
                } else {
                    mindMapService.createNode(payload, { nodes: updatedNodes, edges: updatedEdges });
                    mindMapService.createEdge(newEdge, { nodes: updatedNodes, edges: updatedEdges });
                }
                emitNodeAdd(payload);
            }
            else {
                const newNode = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    position,
                    data: newNodeData,
                    type: type,
                    // style: { zIndex: 999 },
                };
                const updatedNodes = [...nodes, newNode];
                setNodes(updatedNodes);
                recordChange({ nodes, edges }, { nodes: updatedNodes, edges });
                const payload = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    documentId: documentId,
                    name: NEW_NODE_NAME,
                    type: type,
                    position: position,
                    data: newNodeData,
                    // height: 100,
                    // dragging: false,
                    // selected: false,
                    // resizing: false,
                    // style: { width: 100, height: 100 },
                    // width: 100
                };
                mindMapService.createNode(payload, { nodes: updatedNodes, edges });
                emitNodeAdd(payload);
            }
            setIsDocumentChanged(true);
        }
    }, [mindmapInstance, nodes, edges, setEdges, setNodes, mindMapService, isTemplateDocument, isUiLocked, recordChange]);

    // The map when a drag starts: undo puts the dragged nodes back there (D-036).
    const dragStartRef = useRef(null);
    const handleNodeDragStart = () => {
        dragStartRef.current = { nodes, edges };
    };
    const HandleNodeDrag = (event, node, draggedNodes = []) => {
        const start = dragStartRef.current;
        dragStartRef.current = null;
        if (start) {
            const moved = new Map(draggedNodes.map((dragged) => [dragged.id, dragged.position]));
            recordChange(start, {
                nodes: start.nodes.map((n) => (moved.has(n.id) ? { ...n, position: moved.get(n.id) } : n)),
                edges: start.edges,
            });
        }
        if (node && node.position) {
            setNodePosition(node.position);
            setSelectedNodeId(node.id);
        }
    }
    useEffect(() => {
        if (selectedNodeId) {
            let payload = {
                position: nodePosition
            };
            // Build updated nodes to ensure the snapshot reflects the new position
            // even if the React state update hasn't propagated yet.
            const updatedNodes = nodes.map((n) =>
                n.id === selectedNodeId ? { ...n, position: nodePosition } : n
            );
            mindMapService.updateNode(selectedNodeId, payload, { nodes: updatedNodes, edges });

            let listerPayload = {
                id: selectedNodeId,
                position: nodePosition,
                documentId: documentId,
            }
            emitNodeUpdate(listerPayload);
            setIsDocumentChanged(true);
        }
    }, [nodePosition])

    const handelNodeDelete = (e, node) => {
        const filteredEdges = edges.filter(
            (e) => e.source !== selectedNodeId && e.target !== selectedNodeId
        );
        const filteredNodes = nodes.filter((n) => n.id !== selectedNodeId);
        recordChange({ nodes, edges }, { nodes: filteredNodes, edges: filteredEdges });
        mindMapService.deleteNodeWithEdges(selectedNodeId, edges, {
            nodes: filteredNodes,
            edges: filteredEdges,
        });
        setIsDocumentChanged(true);
        emitNodeDelete({ selectedNodeId: selectedNodeId, documentId: documentId, });
    }
    const handelEdgeDelete = (edge) => {
        const filteredEdges = edges.filter((e) => e.id !== selectedEdgeId);
        recordChange({ nodes, edges }, { nodes, edges: filteredEdges });
        mindMapService.deleteEdge(selectedEdgeId, { nodes, edges: filteredEdges });
        emitEdgeDelete({ selectedEdgeId: selectedEdgeId, documentId: documentId, });
        setIsDocumentChanged(true);
    };

    useEffect(() => {
        const saveDocument = async () => {
            if (isDocumentChanged) {
                const data = {
                    nodes: nodes,
                    edges: edges,
                }
                workwiseServices.saveDocumentlastUpdate(documentId, setSavingData, data);
                setIsDocumentChanged(false);
            }
        };
        saveDocument();
    }, [nodes, edges, isDocumentChanged]);

    // While the cached preview is shown, lock editing interactions so stale
    // content can't be mutated before fresh data arrives.
    const editingLocked = isReadOnly || isUiLocked;

    // Right-click menu (D-037); read-only and locked maps keep the browser's own menu.
    const [contextMenu, setContextMenu] = useState(null);
    const closeContextMenu = useCallback(() => setContextMenu(null), []);
    const openContextMenu = (kind) => (event, element) => {
        if (editingLocked) return;
        event.preventDefault();
        setContextMenu({ kind, x: event.clientX, y: event.clientY, [kind]: element });
    };
    const clientToFlow = (point) => {
        const bounds = mindmapWrapper.current.getBoundingClientRect();
        return mindmapInstance.project({ x: point.x - bounds.left, y: point.y - bounds.top });
    };

    return (
        <>
            {(contentLoading && !renderableCacheRef.current) ? (
                <Loader />
            ) : (
                <div className="mindmap-wrapper" ref={mindmapWrapper} style={{ width: '100vw', height: 'calc(100vh - var(--header-height))', backgroundColor: '#fff' }}>
                    <CustomMindmap
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        defaultEdgeOptions={EDGE_OPTIONS}
                        onNodeDoubleClick={handleNodeClick}
                        onNodeClick={(e, node) => { setSelectedNodeId(node.id) }}
                        nodesDraggable={editingLocked ? false : true}
                        nodesConnectable={editingLocked ? false : true}
                        connectedOnClick={editingLocked ? false : true}
                        deleteKeyCode={editingLocked ? null : DELETE_KEYS}
                        connectionMode={ConnectionMode.Loose}
                        onInit={setMindmapInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeDragStart={handleNodeDragStart}
                        onNodeDragStop={HandleNodeDrag}
                        onNodesDelete={handelNodeDelete}
                        onElementsRemove={handelNodeDelete}
                        onNodeMouseEnter={(e, node) => { setSelectedNodeId(node.id); }}
                        proOptions={{ hideAttribution: true }}
                        onEdgesDelete={handelEdgeDelete}
                        onEdgeClick={(e, node) => { setSelectedEdgeId(node.id); }}
                        onNodeContextMenu={openContextMenu('node')}
                        onEdgeContextMenu={openContextMenu('edge')}
                        onPaneContextMenu={openContextMenu('pane')}
                    >
                        {!isReadOnly && (
                            <div className='sideBarDragDropIcons'>
                                <Sidebar />
                            </div>
                        )}
                        <div className='mindMap-options'>
                            <Controls orientation="horizontal" showZoomLevel>
                                <UndoRedoButtons disabled={editingLocked} />
                            </Controls>
                            <MiniMap nodeColor={minimapColorOf} {...MINIMAP_OPTIONS} />
                        </div>
                        <Background variant="dots" gap={12} size={1} />
                    </CustomMindmap>

                    <MapContextMenu
                        menu={contextMenu}
                        onClose={closeContextMenu}
                        onEdit={(node) => handleNodeClick(null, node)}
                        toFlow={clientToFlow}
                    />
                    <MapShortcuts disabled={editingLocked} />
                    {isReadOnly && userInfo.userId !== documentInfo?.creator?.id && (
                        <WaterMark />
                    )}
                </div>
            )}

            {!contentLoading && <NodeProperties nodeId={editingNodeId} onClose={() => setEditingNodeId(null)} />}
        </>
    );
}

export default React.memo(MindMapContainer);